import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ComponentType,
  ReactNode,
  useState,
} from "react";
import PageLayout from "../common/PageLayout";
import NoListingsFound from "./NoListingsFound";
import { Tabs, TabsContent } from "../shadcn/Tabs";
import FetchingSendCardInfoDialog from "src/components/sendCards/FetchingSendCardInfoDialog";
import { PropertySummaryProps } from "../common/PropertySummary";
import ContactCards from "./ContactCards";
import SearchPageMap from "./SearchPageMap";
import { useFeatureDiscovery } from "src/hooks/useFeatureDiscovery";
import { CONTACT_CARD_BUTTONS } from "../common/ContactCard";
import SearchResultCard from "./SearchResultCard";
import { DesktopSearchToolbar } from "./searchToolbar/DesktopSearchToolbar";
import { SearchPageFooter } from "./SearchPageFooter";
import {
  DEFAULT_GUTTER_WIDTH_DESKTOP,
  DEFAULT_GUTTER_WIDTH_MOBILE,
} from "../../constants/ui";
import PropertySummary, {
  OwnerContactInfo,
} from "../common/PropertySummary";
import useIsMobile from "src/hooks/useIsMobile";
import ManageNotes from "../common/ManageNotes";
import ManageTags from "../common/ManageTags";
import PrintListView from "../listings/print/PrintListView";
import PrintOneView from "../listings/print/PrintOneView";
import { SEARCH_PAGE_HEADER } from "../../constants/zIndexes";
import Loading from "./Loading";
import SearchFilters from "./SearchFilters";
import { PropertyTypeOption } from "../common/PropertyTypeFilter";
import ActiveFiltersPanel from "./ActiveFiltersPanel";
import { SearchResultsFormatType } from "./useSearchListingsPage";
import SearchPageTitle from "./SearchPageTitle";
import MobileSearchPageTitle from "./MobileSearchPageTitle";
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "../../constants/ui";
import { Listing } from "src/types/listing";
import Content from "../common/page/Content";
import { useSearchFilters } from "./SearchFilters/useSearchFilters";
import SearchFilterButtons from "./SearchFilters/SearchFilterButtons";
import SearchPageLayout, { SEARCH_TABS, SearchTab } from "./SearchPageLayout";
import { usePanelStack } from "../common/page/usePanelStack";
import { PropertiesListSidebar } from "./PropertiesListSidebar";
import { MobileSearchToolbar } from "./searchToolbar/MobileSearchToolbar";
import { PrintRenderer } from "./PrintRenderer";
import {
  EnumActionType,
  MenuItem,
} from "../neighborhoodSearch/savedSearch/ActionsMenu";
import { Download, Printer, Mail } from "lucide-react";
import VerticalDivider from "../common/VerticalDivider";
import { BatchSelection } from "./BatchSelection";
import { OutputType, PrintExportDialog } from "./PrintExportDialog";
import { formatNumber } from "../../utils/numbers";
import { useSendCards, useSendCardsAccess, useInkedSendCounts } from "src/components/sendCards";

import { MAX_SEND_CARDS_LIMIT } from "src/components/sendCards/useSendCards";
import { useSyncToPartnersMenu } from "src/hooks/useSyncToPartnersMenu";
import { ConfirmDialog } from "src/components/core/ConfirmDialog";

const gutterClassNames = `px-[${DEFAULT_GUTTER_WIDTH_MOBILE}px] md:px-[${DEFAULT_GUTTER_WIDTH_DESKTOP}px]`;

type SearchPageProps = {
  isPro?: boolean;
  pageTitle: string;
  pageTitleIcon?: ReactNode;
  pageTitleButtons?: ReactNode;
  PageTitleProps?: {
    superscriptText?: string;
    adjacentContent?: ReactNode;
    centerContent?: ReactNode;
    subtitle?: string;
    isCondensed?: boolean;
    isUseGutters?: boolean;
    buttonsContent?: ReactNode;
  };
  pageTitleFooter?: string;
  PrintListViewComponentType?: ComponentType<{ listing: Listing }>;
  PrintOneViewComponentType?: ComponentType<{ listing: Listing }>;
  propertyAmenitiesPropsProvider?: (
    listing: Listing
  ) => Partial<PropertySummaryProps>;
  detailsPanelProvider: ({
    detailsListingId,
  }: {
    detailsListingId: string;
  }) => ReactNode;
  searchListings: object;
  searchListingsPage: object;
  isBatchSelectionEnabled?: boolean;
  suppressPriceInMapDetailsPanel?: boolean;
  additionalActionsMenuItems?: MenuItem[];
  sendCardsTemplateCategory?: string;
  propertyTypeOptions?: readonly PropertyTypeOption[];
};

function SearchPage({
  isPro = false,
  pageTitle = "",
  pageTitleIcon,
  pageTitleButtons,
  PageTitleProps = {},
  pageTitleFooter,
  PrintListViewComponentType = PrintListView as unknown as ComponentType<{
    listing: Listing;
  }>,
  PrintOneViewComponentType = PrintOneView as ComponentType<{
    listing: Listing;
  }>,
  propertyAmenitiesPropsProvider,
  detailsPanelProvider,
  searchListings,
  searchListingsPage,
  isBatchSelectionEnabled = true,
  suppressPriceInMapDetailsPanel = false,
  additionalActionsMenuItems = [],
  sendCardsTemplateCategory = "",
  propertyTypeOptions,
}: SearchPageProps) {
  const panelStack = usePanelStack<SearchTab>([SEARCH_TABS.LIST]);
  const {
    open: openPanel,
    close: closePanel,
    closeTop: closeTopPanel,
  } = panelStack;
  const panelCount = panelStack.stack.length;
  const activeTab: SearchTab = panelStack.top ?? SEARCH_TABS.LIST;

  const {
    filters,
    activeFilters,
    currentPage,
    pageSize,
    updateSearchParams,
    resetSearchFilters,
    searchQuery,
    allSearchListingsQuery,
    includePreForeclosureStatusFilter,
    includeDistressedFilters,
    includeListingStatusFilter,
    includeSearchDateTypeFilter,
    includeMLSActivesFilter,
    includeMlsFilter,
    includeLookupSourceFilter,
    includeLookupNameFilter,
    includePropertyTypeFilter,
    suppressPriceFilters,
    importedListingsImports,
    infiniteListings,
    infiniteListingsIsLoading,
    infiniteListingsFetchNextPage,
    infiniteListingsHasNextPage,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = searchListings as any;

  const {
    selectedSort,
    detailsListingId,
    selectedPageListing,
    isSidebarOpen,
    toggleSidebar,
    manageTagsListing,
    printType,
    setPrintType,
    setManageNotesListing,
    setManageTagsListing,
    manageNotesListing,
    printContentRef,
    scrollToTop,
    mapCenter,
    setMapCenter,
    searchResultsFormat,
    setSearchResultsFormat,
    selectListing,
    isBatchSelectionActive,
    toggleIsBatchSelectionActive,
    selectedItems,
    toggleItemIsSelected,
    selectedListings,
    toggleSelectAll,
    setSelectedListingsType,
    addScrollToItem,
    scrollToItemWithId,
    getDetailsListingTags,
    handleDeleteNoteClick,
    handleTagsSaveClick,
    handleAddNoteClick,
    handleToggleContactIsDisabled,
    downloadListings,
    exportListingsToPdf,
    printListings,
    isDownloadingListings,
    isFetchingAllListings,
    isSelectAllChecked,
    printAveryLabels,
    handlePrintSingleListingClick,
    isPrintingSingleListing,
    setIsPrintingSingleListing,
    isPrintExportDialogOpen,
    setIsPrintExportDialogOpen,
    outputType,
    setOutputType,
    selectedExportTemplate,
    setSelectedExportTemplate,
    exportTemplateOptions,
    handlePrintExportDialogOpenChange,
    numPrintRecords,
    getListingsForSendCards,
    getSendCardsCount,
    productType,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = searchListingsPage as any;

  const { sendCards } = useSendCards();
  const hasSendCardsAccess = useSendCardsAccess();
  const [sendCardsProgress, setSendCardsProgress] = useState<{ completed: number; total: number } | null>(null);
  const sendCardsCancelledRef = useRef(false);

  const cancelSendCards = useCallback(() => {
    sendCardsCancelledRef.current = true;
    setSendCardsProgress(null);
  }, []);

  const searchFiltersProps = useSearchFilters(filters, {
    showLookupSourceFilter: includeLookupSourceFilter,
    showLookupNameFilter: includeLookupNameFilter,
    importedListingsImports,
    updateSearchParams,
  });

  const { filterValues, isHideDNCPhoneNumbers } = searchFiltersProps;

  const { data: { total_records: totalRecords, listings } = {}, isLoading } =
    searchQuery;

  // Get all listings for map display
  const { data: allListingsData } = allSearchListingsQuery;
  const allListings = allListingsData?.listings || [];

  // Fetch send counts for current page listings
  const listingsByProduct = useMemo(() => {
    const grouped: Record<string, number[]> = {};
    (listings || []).forEach((listing: Listing) => {
      const product = listing.productType;
      if (!grouped[product]) {
        grouped[product] = [];
      }
      grouped[product].push(Number(listing.id));
    });
    return grouped;
  }, [listings]);

  // Fetch send counts for each product type
  const sendCountsQueries = useMemo(() => {
    return Object.keys(listingsByProduct).map(product => ({
      product,
      ids: listingsByProduct[product],
    }));
  }, [listingsByProduct]);

  // Use the first product type for the hook (we'll need to handle multiple product types differently)
  const firstQuery = sendCountsQueries[0];
  const { data: sendCountsData } = useInkedSendCounts(firstQuery);

  // Create a map of listing ID to send count
  const sendCountsMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (sendCountsData) {
      Object.entries(sendCountsData).forEach(([id, data]) => {
        map[id] = data.count;
      });
    }
    return map;
  }, [sendCountsData]);

  // Calculate the display count for actions menu and dialogs
  const actionCount = useMemo(() => {
    // When batch mode is off, always show total records (all records mode)
    // When batch mode is on, show selected count if items are selected, otherwise show total
    return isBatchSelectionActive
      ? selectedItems.size > 0
        ? selectedItems.size
        : totalRecords || 0
      : totalRecords || 0;
  }, [isBatchSelectionActive, selectedItems.size, totalRecords]);

  // Partner sync hook
  const {
    showPartnerSync,
    partnerSyncMenuItems,
    partnerSyncDialogProps,
  } = useSyncToPartnersMenu({
    productType,
    actionCount,
    getRecordIds: async () => {
      if (isBatchSelectionActive && selectedItems.size > 0) {
        return Array.from(selectedItems).map(id => Number(id));
      }
      const allListingsForSync = await getListingsForSendCards();
      return allListingsForSync.map((l: Listing) => Number(l.id));
    },
  });

  // Action handlers for menu items
  const handlePrint = useCallback(() => {
    setOutputType(OutputType.print);
    setIsPrintingSingleListing(false);
    setIsPrintExportDialogOpen(true);
  }, [setOutputType, setIsPrintingSingleListing, setIsPrintExportDialogOpen]);

  const handleExport = useCallback(() => {
    setOutputType(OutputType.export);
    setIsPrintingSingleListing(false);
    setIsPrintExportDialogOpen(true);
  }, [setOutputType, setIsPrintingSingleListing, setIsPrintExportDialogOpen]);

  const handleSendCardsClick = useCallback(() => {
    // Check count against limit before fetching all listings
    const count = getSendCardsCount();
    if (count > MAX_SEND_CARDS_LIMIT) {
      sendCards([] as Listing[], sendCardsTemplateCategory, count);
      return;
    }
    sendCardsCancelledRef.current = false;
    setSendCardsProgress({ completed: 0, total: 1 });
    getListingsForSendCards(
      (completed: number, total: number) => setSendCardsProgress({ completed, total }),
      sendCardsCancelledRef,
    ).then((listingsForCards: Listing[]) => {
      if (!sendCardsCancelledRef.current && listingsForCards.length > 0) {
        sendCards(listingsForCards, sendCardsTemplateCategory);
      }
    }).finally(() => setSendCardsProgress(null));
  }, [getSendCardsCount, sendCards, sendCardsTemplateCategory, getListingsForSendCards]);

  // Create actions menu items for search page
  const actionsMenuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        type: EnumActionType.Print,
        icon: <Printer className="h-4 w-4" />,
        label: `Print (${formatNumber(actionCount, "0")})`,
        onAction: handlePrint,
      },
      {
        type: EnumActionType.Export,
        icon: <Download className="h-4 w-4" />,
        label: `Export (${formatNumber(actionCount, "0")})`,
        onAction: handleExport,
      },
    ];

    if (hasSendCardsAccess) {
      items.push({
        type: EnumActionType.SendCard,
        icon: <Mail className="h-4 w-4" />,
        label: `Send Card (${formatNumber(actionCount, "0")})`,
        disabled: actionCount === 0,
        onAction: handleSendCardsClick,
      });
    }

    // Add partner sync items from hook
    if (showPartnerSync) {
      items.push(...partnerSyncMenuItems);
    }

    return [...items, ...additionalActionsMenuItems];
  }, [actionCount, additionalActionsMenuItems, hasSendCardsAccess, showPartnerSync, partnerSyncMenuItems, handlePrint, handleExport, handleSendCardsClick]);

  const isMobile = useIsMobile();

  const isExistSearchResultsWithContacts = useMemo(
    () =>
      (listings || []).some(
        (listing: Listing) => listing?.contacts && listing.contacts.length > 0
      ),
    [listings]
  );

  const features = useMemo(
    () => [
      ...(!isMobile
        ? [
            "my-location-button-desktop",
            "search-results-format-desktop",
          ]
        : []),
      ...(isMobile
        ? [
            "my-location-button-mobile",
            "search-results-format-mobile",
          ]
        : []),
      "print-and-download",
      ...(isExistSearchResultsWithContacts
        ? [
            "contact-disable-desktop",
            "contact-copy-desktop",
            "contact-call-desktop",
          ]
        : []),
    ],
    [isMobile, isExistSearchResultsWithContacts]
  );

  const { currentFeature, nextFeature, isLastFeature } =
    useFeatureDiscovery(features);

  const handleSearchParamsUpdated = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (updates: Record<string, any>) => {
      updateSearchParams(updates);
      scrollToTop();
    },
    [updateSearchParams, scrollToTop]
  );

  const handleFiltersUpdated = useCallback(() => {
    handleSearchParamsUpdated({ ...filterValues, currentPage: 1 });
    scrollToTop();
  }, [handleSearchParamsUpdated, scrollToTop, filterValues]);

  const handleSelectListing = useCallback(
    (listing: Listing, isSuppressMapCenter = false) => {
      selectListing(listing);
      openPanel(SEARCH_TABS.DETAILS);
      toggleSidebar(true);
      if (!isSuppressMapCenter) {
        setMapCenter(listing.geolocation);
      }
    },
    [selectListing, setMapCenter, openPanel, toggleSidebar]
  );

  const handleListingImageClick = useCallback(
    (listing: Listing) => {
      selectListing(listing, true);
      setMapCenter(listing.geolocation);
    },
    [selectListing, setMapCenter]
  );

  const handleBackToListClick = useCallback(() => {
    // In list view the list is the main content, so the sidebar closes instead
    if (searchResultsFormat === SearchResultsFormatType.map) {
      panelStack.reset([SEARCH_TABS.LIST]);
    } else {
      const remaining = panelStack.stack.filter(
        (tab) => tab !== SEARCH_TABS.DETAILS
      );
      panelStack.reset(remaining);
      if (!remaining.length) {
        toggleSidebar(false);
      }
    }
    // Clear the selected listing to show the list view
    selectListing(null);
  }, [selectListing, panelStack, searchResultsFormat, toggleSidebar]);

  // Create dynamic sidebar titles
  const listTitle = useMemo(() => {
    return (
      <span className="text-sm font-semibold !text-gray-500">
        Properties ({totalRecords || 0})
      </span>
    );
  }, [totalRecords]);

  const detailsTitle = useMemo(() => {
    if (selectedPageListing) {
      return (
        <button
          onClick={handleBackToListClick}
          className="flex flex-row items-center gap-2 text-sm font-semibold !text-gray-500 hover:text-gray-900 transition-colors"
        >
          <span>←</span>
          <span>Back to List</span>
        </button>
      );
    }
    return "Details";
  }, [selectedPageListing, handleBackToListClick]);

  const contactCardDiscoveryTooltip = useMemo(() => {
    if (currentFeature === "contact-disable-desktop") {
      return CONTACT_CARD_BUTTONS.disable;
    }
    if (currentFeature === "contact-call-desktop") {
      return CONTACT_CARD_BUTTONS.call;
    }
    if (currentFeature === "contact-copy-desktop") {
      return CONTACT_CARD_BUTTONS.copy;
    }
    return "";
  }, [currentFeature]);

  const handleFilterButtonClick = useCallback(() => {
    if (activeTab === SEARCH_TABS.FILTERS && isSidebarOpen) {
      // Reveal the panel underneath, or close the sidebar if there is none
      closePanel(SEARCH_TABS.FILTERS);
      if (panelCount <= 1) {
        toggleSidebar(false);
      }
    } else {
      openPanel(SEARCH_TABS.FILTERS);
      toggleSidebar(true);
    }
  }, [activeTab, isSidebarOpen, panelCount, closePanel, openPanel, toggleSidebar]);

  const openFilters = useCallback(() => {
    openPanel(SEARCH_TABS.FILTERS);
    toggleSidebar(true);
  }, [openPanel, toggleSidebar]);

  // X / Close pops the top panel; the sidebar closes once the stack is empty
  const handleSidebarToggle = useCallback(
    (isOpen?: boolean) => {
      const nextOpen = isOpen ?? !isSidebarOpen;
      if (nextOpen) {
        if (!panelCount) {
          openPanel(
            searchResultsFormat === SearchResultsFormatType.map
              ? SEARCH_TABS.LIST
              : SEARCH_TABS.FILTERS
          );
        }
        toggleSidebar(true);
        return;
      }
      if (activeTab === SEARCH_TABS.DETAILS) {
        handleBackToListClick();
      } else if (panelCount > 1) {
        closeTopPanel();
      } else {
        toggleSidebar(false);
      }
    },
    [
      isSidebarOpen,
      panelCount,
      activeTab,
      searchResultsFormat,
      openPanel,
      closeTopPanel,
      toggleSidebar,
      handleBackToListClick,
    ]
  );

  const isSidebarOpenRef = useRef(isSidebarOpen);
  isSidebarOpenRef.current = isSidebarOpen;

  useEffect(() => {
    if (searchResultsFormat === SearchResultsFormatType.map) {
      if (!isSidebarOpenRef.current) {
        panelStack.reset([SEARCH_TABS.LIST]);
        toggleSidebar(true);
      }
    } else {
      const remaining = panelStack.stack.filter(
        (tab) => tab !== SEARCH_TABS.LIST
      );
      panelStack.reset(remaining);
      if (!remaining.length) {
        toggleSidebar(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsFormat]);

  // Create content for each tab
  const filtersContent = (
    <SearchFilters
      onComplete={handleFiltersUpdated}
      showListingStatus={includeListingStatusFilter}
      showSearchDateType={includeSearchDateTypeFilter}
      showMlsFilter={includeMlsFilter}
      showIncludeActiveOnMLS={includeMLSActivesFilter}
      suppressPriceFilters={suppressPriceFilters}
      showDistressedFilters={includeDistressedFilters}
      showPreForeclosureStatus={includePreForeclosureStatusFilter}
      showLookupNameFilter={includeLookupNameFilter}
      showLookupSourceFilter={includeLookupSourceFilter}
      showPropertyTypeFilter={includePropertyTypeFilter}
      propertyTypeOptions={propertyTypeOptions}
      {...searchFiltersProps}
      resetFilters={resetSearchFilters}
    />
  );

  // Handle infinite scroll
  const handleScrollBottomReached = () => {
    if (infiniteListingsHasNextPage && !infiniteListingsIsLoading) {
      infiniteListingsFetchNextPage();
    }
  };

  const listContent = (
    <PropertiesListSidebar
      listings={infiniteListings || []}
      selectedListing={selectedPageListing}
      selectedListingId={detailsListingId}
      suppressInlineDetails={!!detailsPanelProvider}
      onListingClick={handleSelectListing}
      onListingImageClick={searchResultsFormat === SearchResultsFormatType.map ? handleListingImageClick : undefined}
      onAddNoteClick={() =>
        setManageNotesListing(selectedPageListing as Listing)
      }
      onAddTagClick={() => setManageTagsListing(selectedPageListing as Listing)}
      isLoadingSelectedListing={false}
      isLoadingListings={infiniteListingsIsLoading}
      onScrollBottomReached={handleScrollBottomReached}
      onAdjustFiltersClick={handleFilterButtonClick}
      onClearFiltersClick={resetSearchFilters}
      isHideDNCPhoneNumbers={false}
      isFavoritingEnabled={false}
    />
  );

  const detailsContent =
    detailsListingId && detailsPanelProvider
      ? detailsPanelProvider({
          detailsListingId,
        })
      : null;

  return (
    <section className="hideScrollBar">
      <PageLayout suppressGutters suppressTopPadding>
        <SearchPageLayout
          filtersContent={filtersContent}
          listContent={listContent}
          detailsContent={detailsContent}
          activeTab={activeTab}
          onTabChange={(tab) => openPanel(tab)}
          onBackToList={handleBackToListClick}
          stack={panelStack.stack}
          isSidebarOpen={isSidebarOpen}
          onSidebarToggle={handleSidebarToggle}
          filtersTitle="Filters"
          filtersFooter={<SearchFilterButtons onReset={resetSearchFilters} />}
          listTitle={listTitle}
          detailsTitle={detailsTitle}
          sidebarOffsetHeight={50}
          sidebarSize="sm"
        >
          <Content>
            {manageNotesListing && (
              <ManageNotes
                key={`manage-notes-${manageNotesListing?.id}`}
                onDeleteClick={(noteId) => {
                  handleDeleteNoteClick({
                    listingId: manageNotesListing.id,
                    noteId,
                  });
                }}
                onAddClick={(noteText) => {
                  handleAddNoteClick({
                    listingId: manageNotesListing.id,
                    note: noteText,
                  });
                }}
                onClose={() => setManageNotesListing(null)}
                open
                notes={manageNotesListing?.notes || []}
              />
            )}
            {manageTagsListing && (
              <ManageTags
                key={`manage-tags-${manageTagsListing?.id}`}
                onClose={() => setManageTagsListing(null)}
                onSaveClick={(updatedTags) => {
                  handleTagsSaveClick({
                    listingId: manageTagsListing.id,
                    tags: updatedTags,
                  });
                }}
                open={manageTagsListing}
                tags={getDetailsListingTags({
                  detailsListingId: manageTagsListing.id,
                })}
              />
            )}
            <div
              className={`search-page-main-content hideScrollBar ${
                activeTab === SEARCH_TABS.DETAILS
                  ? "print:h-screen overflow-hidden"
                  : ""
              }`}
            >
              <div
                className={`print:pt-0 md:sticky pb-3 md:left-[240px] right-0 top-0 md:top-0 bg-white ${
                  activeTab === SEARCH_TABS.DETAILS ? "" : "print:hidden"
                } hideScrollBar `}
                style={{
                  zIndex: SEARCH_PAGE_HEADER,
                  left: `${
                    isMobile
                      ? 0
                      : isSidebarOpen
                      ? SIDEBAR_EXPANDED_WIDTH
                      : SIDEBAR_COLLAPSED_WIDTH
                  }px`,
                }}
              >
                <div className={`search-page-top ${gutterClassNames}`}>
                  {/* Mobile title */}
                  <div className="mobile-search-page-title md:hidden">
                    <MobileSearchPageTitle
                      title={pageTitle}
                      titleIcon={pageTitleIcon}
                      isPro={isPro}
                      subtitle={PageTitleProps.subtitle}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      totalRecords={totalRecords}
                      isLoading={isLoading}
                      actionsMenuItems={actionsMenuItems}
                      isBatchSelectionActive={isBatchSelectionActive}
                    />
                  </div>
                  {/* Desktop title */}
                  <div className="desktop-search-page-title hidden md:block">
                    <SearchPageTitle
                      title={pageTitle}
                      titleIcon={pageTitleIcon}
                      isPro={isPro}
                      subtitle={PageTitleProps.subtitle}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      totalRecords={totalRecords}
                      isLoading={isLoading}
                      searchResultsFormat={searchResultsFormat}
                      setSearchResultsFormat={setSearchResultsFormat}
                      buttonsContent={
                        pageTitleButtons || PageTitleProps.buttonsContent
                      }
                      actionsMenuItems={actionsMenuItems}
                      onSendCardClick={hasSendCardsAccess ? handleSendCardsClick : undefined}
                      sendCardCount={actionCount}
                      isBatchSelectionActive={isBatchSelectionActive}
                      {...PageTitleProps}
                    />
                  </div>
                  {pageTitleFooter}

                  <div className="md:hidden mt-1">
                    {/* Mobile panel */}
                    <MobileSearchToolbar
                      isDetailsOpen={activeTab === SEARCH_TABS.DETAILS}
                      currentFeature={currentFeature || ""}
                      searchResultsFormat={searchResultsFormat}
                      totalRecords={totalRecords}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      setSearchResultsFormat={setSearchResultsFormat}
                      setMapCenter={setMapCenter}
                      selectedPageListing={selectedPageListing}
                      scrollToItemWithId={scrollToItemWithId}
                      onSelectListing={handleSelectListing}
                      listings={listings}
                      nextFeature={nextFeature}
                      isLastFeature={isLastFeature}
                      onSearchParamsUpdated={handleSearchParamsUpdated}
                      isBatchSelectionActive={isBatchSelectionActive}
                      onBatchSelectionToggle={toggleIsBatchSelectionActive}
                      selectedSort={selectedSort}
                      onFilterButtonClick={handleFilterButtonClick}
                    />
                  </div>

                  {/* Filters panel */}
                  <div className="hidden md:block">
                    <DesktopSearchToolbar
                      setMapCenter={setMapCenter}
                      setSearchResultsFormat={setSearchResultsFormat}
                      searchResultsFormat={searchResultsFormat}
                      selectedPageListing={selectedPageListing}
                      scrollToItemWithId={scrollToItemWithId}
                      onSelectListing={handleSelectListing}
                      listings={listings}
                      selectedSort={selectedSort}
                      onSearchParamsUpdated={handleSearchParamsUpdated}
                      currentFeature={currentFeature || undefined}
                      nextFeature={nextFeature}
                      isLastFeature={isLastFeature}
                      totalRecords={totalRecords}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      isSuppressSearchResultsFormatSelector
                      onFilterButtonClick={handleFilterButtonClick}
                      isBatchSelectionActive={isBatchSelectionActive}
                      onBatchSelectionToggle={toggleIsBatchSelectionActive}
                    />
                  </div>
                  <div className="mt-3">
                    <div className="flex flex-row gap-2 items-center">
                      {isBatchSelectionEnabled && (
                        <>
                          <BatchSelection
                            isSelectAllChecked={isSelectAllChecked}
                            toggleSelectAll={toggleSelectAll}
                            onListingsTypeClick={setSelectedListingsType}
                            isBatchSelectionActive={isBatchSelectionActive}
                          />
                          {isBatchSelectionActive && <VerticalDivider />}
                        </>
                      )}
                      <ActiveFiltersPanel
                        activeFilters={activeFilters}
                        suppressLabel={isMobile}
                        suppressWrapping={isMobile}
                        onFilterClick={openFilters}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <section className="content-pane relative">
                <Tabs value={searchResultsFormat} className="h-full">
                  <TabsContent
                    value={SearchResultsFormatType.map}
                    className="h-full mt-0"
                  >
                    <section
                      className="map-content h-full"
                      style={{
                        height: "100vh",
                      }}
                    >
                      <SearchPageMap
                        mapCenter={mapCenter}
                        listings={allListings}
                        selectedListing={selectedPageListing}
                        isHideDNCPhoneNumbers={isHideDNCPhoneNumbers}
                        onMarkerClick={(listing) => {
                          handleSelectListing(listing, true);
                        }}
                        suppressPriceInDetailsPanel={
                          suppressPriceInMapDetailsPanel
                        }
                      />
                    </section>
                  </TabsContent>
                  <TabsContent
                    value={SearchResultsFormatType.list}
                    className="mt-0"
                  >
                    <section className="search-results-content">
                      {isLoading && (
                        <div className={`${gutterClassNames} pt-3`}>
                          <Loading />
                        </div>
                      )}
                      {listings?.map((listing: Listing, index: number) => {
                        const listingId = listing.id;
                        const selectedPageListingId = selectedPageListing?.id;
                        return (
                          <SearchResultCard
                            key={`search-result-${listingId}`}
                            isSelected={listingId === selectedPageListingId}
                            ref={(element) => {
                              if (element) {
                                addScrollToItem(element, listingId, index);
                              }
                            }}
                            className={gutterClassNames}
                            isBatchSelectionActive={isBatchSelectionActive}
                            isChecked={selectedItems.has(listingId)}
                            onCheckedChange={() =>
                              toggleItemIsSelected(listingId)
                            }
                          >
                            <PropertySummary
                              listing={listing}
                              isTagsAndNotesEnabled
                              sendCardsTemplateCategory={sendCardsTemplateCategory}
                              sendCardCount={sendCountsMap[listingId] || 0}
                              onPrintClick={() =>
                                handlePrintSingleListingClick(listing)
                              }
                              onTitleClick={() => handleSelectListing(listing)}
                              onOpenNotesClick={() =>
                                setManageNotesListing(listing)
                              }
                              onOpenTagsClick={() =>
                                setManageTagsListing(listing)
                              }
                              {...(propertyAmenitiesPropsProvider
                                ? propertyAmenitiesPropsProvider(listing)
                                : {})}
                            />

                            <OwnerContactInfo {...listing?.ownerContactInfo} />

                            <ContactCards
                              onToggleContactIsDisabled={
                                handleToggleContactIsDisabled
                              }
                              contacts={(listing?.contacts || []).filter(
                                (contact) =>
                                  isHideDNCPhoneNumbers ? !contact.dnc : true
                              )}
                              {...(index === 0 &&
                                listing?.contacts?.length &&
                                contactCardDiscoveryTooltip && {
                                  discoveryTooltip: contactCardDiscoveryTooltip,
                                  isLastDiscoveryTooltip: isLastFeature,
                                  onDiscoveryTooltipNextClick: nextFeature,
                                })}
                            />
                          </SearchResultCard>
                        );
                      })}
                    </section>
                    {!totalRecords && !isLoading && (
                      <div className={gutterClassNames}>
                        <NoListingsFound
                          onAdjustFiltersClick={handleFilterButtonClick}
                          onClearFiltersClick={resetSearchFilters}
                        />
                      </div>
                    )}
                    <SearchPageFooter
                      totalRecords={totalRecords}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      gutterClassNames={gutterClassNames}
                      onSearchParamsUpdated={handleSearchParamsUpdated}
                    />
                  </TabsContent>
                </Tabs>
                <section
                  ref={printContentRef}
                  className="hidden print:block m-4"
                >
                  {selectedListings && (
                    <PrintRenderer
                      printType={printType}
                      listings={selectedListings}
                      PrintListViewComponentType={PrintListViewComponentType}
                      PrintOneViewComponentType={PrintOneViewComponentType}
                    />
                  )}
                </section>
              </section>
            </div>
            <FetchingSendCardInfoDialog
              progress={sendCardsProgress}
              onCancel={cancelSendCards}
              cardCount={actionCount}
            />
            {isPrintExportDialogOpen && (
              <PrintExportDialog
                open
                onOpenChange={handlePrintExportDialogOpenChange}
                outputType={outputType}
                numPrintRecords={numPrintRecords}
                onListingsCsvExportClick={downloadListings}
                onListingsPdfExportClick={exportListingsToPdf}
                onListingsPdfPrintClick={printListings}
                onLabelsPrintClick={() => printAveryLabels("print")}
                onLabelsExportClick={() => printAveryLabels("download")}
                printType={printType}
                onPrintTypeChange={setPrintType}
                isDownloadingListings={isDownloadingListings}
                isSuppressPrintTypeSelection={isPrintingSingleListing}
                isSuppressAveryLabels={isPrintingSingleListing}
                isFetchingListings={isFetchingAllListings}
                exportTemplateOptions={exportTemplateOptions}
                selectedExportTemplate={selectedExportTemplate}
                onExportTemplateChange={setSelectedExportTemplate}
              />
            )}
            <ConfirmDialog {...partnerSyncDialogProps} />
          </Content>
        </SearchPageLayout>
      </PageLayout>
    </section>
  );
}

export default SearchPage;
